import {
  ORGANIZATION_GROUP_PAGE,
  GET_ORGANIZATION_GROUP,
  FETCH_ORGANIZATION_GROUP,
  UPDATE_ORGANIZATION_GROUP,
  DELETE_ORGANIZATION_GROUP,
  BULK_UPDATE_ORGANIZATION_GROUP,
  ADD_ORGANIZATION_GROUP_MEMBER,
  REMOVE_ORGANIZATION_GROUP_MEMBER,
  ORGANIZATION_GROUP_CATEGORY_PREVIEW,
  ORGANIZATION_GROUP_CATEGORY_CONFIRMED,
  PENDING_ORGANIZATION_GROUP,
  CLEAR_ORGANIZATION_GROUP,
} from "../constants/actions";

const initialState = {
  organization_groups: [],
  organization_groups_by_type: {
    survey: [],
    permission: [],
  },
  organization_groups_by_type_meta: {
    survey: null, // { organization, page_size, fetchedAt }
    permission: null, // { organization, page_size, fetchedAt }
  },
  organization_group: {},
  pending: false,
  page: {},
  last_action: null,
  lastAffectedGroupId: null,
  lastUpdatedAt: null,
  categoryPreview: {
    groupId: null,
    mode: null, // "add" | "remove" | null
    stage: null, // "preview" | "confirmed" | null
    match_count: 0,
    sample_count: 0,
    employee_ids: [],
    samples: [],
    filters_used: null,
    remove_tokens: false,
    message: null,
  },
};

export default function OrganizationGroup(state = initialState, action) {
  switch (action.type) {
    case ORGANIZATION_GROUP_PAGE:
      return {
        ...state,
        page: action.payload,
      };

    case GET_ORGANIZATION_GROUP:
      // Backward compatible: keep the existing flat list updated as before.
      // New: also cache results by group_type when the saga provides it.
      const incomingList = action.payload?.results || action.payload || [];
      const groupType = action?.meta?.group_type;
      const shouldCacheByType = groupType === 'survey' || groupType === 'permission';
      const incomingOrg = action?.meta?.organization ?? null;
      const incomingPageSize = Number(action?.payload?.page_size || action?.meta?.page_size || 0) || null;

      // For AutomatedReports we want a stable "full list" cache (page_size=5000) that
      // doesn't get overwritten by other screens fetching a small paginated list (page_size=12).
      // Note: we still always update the flat `organization_groups` for other screens.
      const prevByTypeList = shouldCacheByType ? state.organization_groups_by_type?.[groupType] : null;
      const prevByTypeMeta = shouldCacheByType ? state.organization_groups_by_type_meta?.[groupType] : null;
      const sameOrg =
        incomingOrg == null ||
        prevByTypeMeta?.organization == null ||
        String(prevByTypeMeta.organization) === String(incomingOrg);

      let nextByTypeList = incomingList;
      let nextByTypeMeta = shouldCacheByType ? prevByTypeMeta : null;

      if (shouldCacheByType) {
        const prevLen = Array.isArray(prevByTypeList) ? prevByTypeList.length : 0;
        const nextLen = Array.isArray(incomingList) ? incomingList.length : 0;

        // Keep the larger cached list for the same org if a smaller page comes in.
        if (sameOrg && prevLen > nextLen) {
          nextByTypeList = prevByTypeList;
        } else {
          nextByTypeMeta = {
            organization: incomingOrg,
            page_size: incomingPageSize,
            fetchedAt: Date.now(),
          };
        }
      }

      const organization_groups_by_type = shouldCacheByType
        ? {
            ...state.organization_groups_by_type,
            [groupType]: nextByTypeList,
          }
        : state.organization_groups_by_type;

      const organization_groups_by_type_meta = shouldCacheByType
        ? {
            ...state.organization_groups_by_type_meta,
            [groupType]: nextByTypeMeta,
          }
        : state.organization_groups_by_type_meta;
      
      return {
        ...state,
        organization_groups: incomingList,
        organization_groups_by_type,
        organization_groups_by_type_meta,
        page: {
          count: action.payload?.count || 0,
          next: action.payload?.links?.next || action.payload?.next || null,
          previous: action.payload?.links?.previous || action.payload?.previous || null,
          page_size: action.payload?.page_size || 12,
        },
      };

    case FETCH_ORGANIZATION_GROUP:
      return {
        ...state,
        organization_group: action.payload,
        last_action: 'FETCH_SINGLE',
        lastAffectedGroupId: action.payload?.id,
        lastUpdatedAt: Date.now(),
      };

    case UPDATE_ORGANIZATION_GROUP:
      // Check if the organization group already exists in the list
      const existingIndex = state.organization_groups.findIndex(
        (group) => group.id === action.payload.id
      );

      if (existingIndex !== -1) {
        // Update existing organization group
        const updatedGroups = [...state.organization_groups];
        updatedGroups[existingIndex] = action.payload;
        return {
          ...state,
          organization_groups: updatedGroups,
          organization_group: action.payload,
          last_action: 'CREATE',
          lastAffectedGroupId: action.payload?.id,
          lastUpdatedAt: Date.now(),
        };
      } else {
        // Add new organization group
        return {
          ...state,
          organization_groups: [...state.organization_groups, action.payload],
          organization_group: action.payload,
          last_action: 'CREATE',
          lastAffectedGroupId: action.payload?.id,
          lastUpdatedAt: Date.now(),
        };
      }

    case DELETE_ORGANIZATION_GROUP:
      return {
        ...state,
        organization_groups: state.organization_groups.filter(
          (group) => group.id !== action.payload.id
        ),
        organization_group: 
          state.organization_group.id === action.payload.id
            ? {}
            : state.organization_group,
      };

    case BULK_UPDATE_ORGANIZATION_GROUP:
      // Bulk update completed successfully - data will be refreshed by saga
      return {
        ...state,
        last_action: 'BULK',
        lastAffectedGroupId: action.payload?.organization_group_id ?? state.organization_group?.id,
        lastUpdatedAt: Date.now(),
      };

    case ADD_ORGANIZATION_GROUP_MEMBER:
      // Individual member added successfully - data will be refreshed by saga
      return {
        ...state,
        last_action: 'ADD_MEMBER',
        lastAffectedGroupId: action.payload?.group_id,
        lastUpdatedAt: Date.now(),
      };

    case REMOVE_ORGANIZATION_GROUP_MEMBER:
      // Individual member removed successfully - data will be refreshed by saga
      return {
        ...state,
        last_action: 'REMOVE_MEMBER',
        lastAffectedGroupId: action.payload?.group_id,
        lastUpdatedAt: Date.now(),
      };

    case ORGANIZATION_GROUP_CATEGORY_PREVIEW:
      return {
        ...state,
        last_action: "CATEGORY_PREVIEW",
        lastAffectedGroupId: action.payload?.organization_group_id || state.lastAffectedGroupId,
        lastUpdatedAt: Date.now(),
        categoryPreview: {
          groupId: action.payload?.organization_group_id ?? null,
          mode: action.payload?.mode ?? null, // "add" or "remove"
          stage: action.payload?.data?.stage || "preview",
          match_count: action.payload?.data?.match_count ?? 0,
          sample_count: action.payload?.data?.sample_count ?? 0,
          employee_ids: action.payload?.data?.employee_ids || [],
          samples: action.payload?.data?.samples || [],
          filters_used: action.payload?.data?.filters_used || null,
          remove_tokens: !!action.payload?.remove_tokens,
          message: action.payload?.data?.message || null,
        },
      };

    case ORGANIZATION_GROUP_CATEGORY_CONFIRMED:
      return {
        ...state,
        last_action: "CATEGORY_CONFIRMED",
        lastAffectedGroupId: action.payload?.organization_group_id || state.lastAffectedGroupId,
        lastUpdatedAt: Date.now(),
        categoryPreview: {
          // Keep the last preview info, but update stage + message and counts if present
          ...state.categoryPreview,
          groupId: action.payload?.organization_group_id ?? state.categoryPreview.groupId,
          mode: action.payload?.mode ?? state.categoryPreview.mode,
          stage: action.payload?.data?.stage || "confirmed",
          message: action.payload?.data?.message || state.categoryPreview.message,
          match_count: action.payload?.data?.match_count ?? state.categoryPreview.match_count,
          sample_count: action.payload?.data?.sample_count ?? state.categoryPreview.sample_count,
          employee_ids: action.payload?.data?.employee_ids || state.categoryPreview.employee_ids,
          samples: action.payload?.data?.samples || state.categoryPreview.samples,
          filters_used: action.payload?.data?.filters_used || state.categoryPreview.filters_used,
          remove_tokens: !!action.payload?.remove_tokens,
        },
      };

    case PENDING_ORGANIZATION_GROUP:
      return {
        ...state,
        pending: action.payload,
      };

    case CLEAR_ORGANIZATION_GROUP:
      return {
        ...state,
        organization_groups: [],
        organization_group: {},
        pending: false,
        page: {},
      };

    default:
      return state;
  }
}